import {CODE_NOT_REDEEMABLE} from '@devmil/contracts';

import {isPanelApiError} from '../data/PanelDataSource.js';

/**
 * Tradução de recusa da API para o que a tela precisa fazer.
 *
 * Duas decisões moram aqui, e as duas são cerca:
 *
 * 1. **A mensagem é NOSSA, nunca a do servidor.** O corpo de erro de uma rota
 *    de resgate pode ecoar o que foi enviado, e o que foi enviado é o código do
 *    titular e o telefone dele. Um envelope que volte com o valor dentro
 *    apareceria na tela, no print que alguém cola no chat e no relatório de
 *    erro. Perder o detalhe do servidor é o preço, e é barato.
 * 2. **Lista de permissão.** Status conhecido tem mensagem própria; status
 *    desconhecido cai na mensagem genérica E sem precondição resolvida, que é o
 *    lado restritivo. Uma lista de bloqueio deixaria um status novo passar
 *    calado como se fosse sucesso parcial.
 */

/** A prova que falta para a ação acontecer. São coisas diferentes. */
export type MissingProof =
  /** Thiago precisa confirmar o passkey de novo. Sessão viva não basta. */
  | 'passkey_reauth'
  /** O TITULAR precisa provar que é ele, pelo canal dele. */
  | 'subject_verification';

export interface PanelProblem {
  message: string;
  /** `null` quando a recusa não é de precondição, e sim erro comum. */
  missingProof: MissingProof | null;
}

/** Códigos que a API usa para dizer "confirme o passkey de novo". */
const CODIGOS_DE_REAUTENTICACAO = new Set([
  'REAUTH_REQUIRED',
  'RECENT_REAUTH_REQUIRED',
]);

/**
 * Códigos que a API usa para dizer "falta a prova do titular".
 *
 * Os quatro últimos são os `428` de `RECUSA_DA_EXECUCAO`
 * (`privacy-request-service.ts`). Faltavam três deles aqui, e o efeito era o
 * pior possível: a recusa caía no desempate, virava "confirme o passkey", o
 * formulário de verificação nunca abria, e cada nova tentativa recebia o mesmo
 * `428` para sempre. Códigos novos entram nesta lista, nunca no desempate.
 */
const CODIGOS_DE_TITULAR = new Set([
  'SUBJECT_VERIFICATION_REQUIRED',
  'SUBJECT_NOT_VERIFIED',
  'VERIFICATION_CHALLENGE_NOT_CONSUMED',
  'VERIFICATION_EVIDENCE_MISSING',
  'VERIFICATION_CHANNEL_MISMATCH',
]);

const MENSAGEM = {
  reauth:
    'Esta ação exige confirmação por passkey. Confirme sua identidade e tente de novo.',
  titular:
    'Falta a prova do titular. Verifique o titular pelo canal dele antes de executar o direito.',
  generico: 'Não foi possível concluir a ação. Tente de novo em instantes.',
} as const;

/**
 * Quando a causa da falta de prova é conhecida, ela é dita.
 *
 * "Falta a prova do titular" manda verificar de novo, e verificar de novo pelo
 * mesmo caminho é o que NÃO resolve estes dois casos. A frase específica é a
 * diferença entre o operador emitir um desafio novo no canal certo e ele repetir
 * a mesma tentativa até o prazo do Art. 18 vencer.
 */
const MENSAGEM_DO_TITULAR: Record<string, string> = {
  VERIFICATION_CHANNEL_MISMATCH:
    'A prova voltou por um canal diferente do que originou o pedido. Emita um desafio novo pelo canal em que o pedido chegou.',
  VERIFICATION_CHALLENGE_NOT_CONSUMED:
    'Nenhum desafio de verificação foi consumido neste pedido. Emita o desafio e espere a volta do titular.',
  VERIFICATION_EVIDENCE_MISSING:
    'Falta a evidência da volta pelo canal do titular. Registre a evidência ao verificar.',
};

const POR_STATUS: Record<number, string> = {
  401: 'Sessão vencida. Entre de novo no painel para continuar.',
  403: 'Esta sessão não tem permissão para esta ação.',
  404: 'Não encontramos este registro. Ele pode já ter sido apagado por retenção.',
  409: 'O estado mudou desde que a tela carregou. Recarregue antes de decidir de novo.',
  422: 'Os dados não foram aceitos no formato exigido. Confira e tente de novo.',
  429: 'Tentativas demais em pouco tempo. Espere antes de tentar de novo.',
};

/**
 * Códigos de conflito com saída própria.
 *
 * `409` genérico manda recarregar, e recarregar não resolve nenhum destes: o
 * estado não mudou, a ação é que precisa ser outra. Sem isto, o operador
 * recarrega e tenta de novo indefinidamente.
 */
const MENSAGEM_POR_CODIGO: Record<string, string> = {
  WHATSAPP_PHONE_ALREADY_LINKED:
    'Este telefone já tem vínculo ativo em outra conversa. Desvincule lá antes de resgatar aqui.',
  // Este pedido já abriu a eliminação dele: executar de novo não abre outra.
  // Um SEGUNDO pedido do mesmo titular não cai aqui, ele passa a andar junto da
  // lápide que já existe e a tela mostra a propagação dela.
  PRIVACY_ERASURE_ALREADY_STARTED:
    'A eliminação deste pedido já foi aberta. Acompanhe a propagação em vez de executar de novo.',
};

export interface ProblemContext {
  /**
   * Se o titular deste pedido já provou ser ele.
   *
   * Serve para desempatar uma precondição sem código conhecido: se a prova do
   * titular comprovadamente não existe, é ELA que falta. Deduzir do dado que
   * temos é melhor que adivinhar pelo texto do código.
   */
  subjectVerified?: boolean;
}

/**
 * Qual prova falta, com o desempate no lado restritivo.
 *
 * O desempate mudou depois da auditoria, e o motivo é o que o próprio arquivo
 * já dizia sobre lista de permissão: `428` nesta API é SEMPRE precondição do
 * titular (os quatro códigos vivem em `RECUSA_DA_EXECUCAO`), e "confirme o
 * passkey" é a ação que não resolve nenhuma delas. Um código `428` novo que
 * ninguém tenha escrito aqui cai em "falta a prova do titular", que no pior
 * caso pede uma prova a mais; cair no passkey pedia a errada, escondia a causa
 * e não abria o formulário de verificação.
 */
const precondicao = (
  status: number,
  code: string,
  {subjectVerified}: ProblemContext,
): MissingProof => {
  if (CODIGOS_DE_REAUTENTICACAO.has(code)) {
    return 'passkey_reauth';
  }
  if (CODIGOS_DE_TITULAR.has(code)) {
    return 'subject_verification';
  }
  if (status === 428) {
    return 'subject_verification';
  }
  return subjectVerified === false ? 'subject_verification' : 'passkey_reauth';
};

export const describeProblem = (
  error: unknown,
  context: ProblemContext = {},
): PanelProblem => {
  if (!isPanelApiError(error)) {
    return {message: MENSAGEM.generico, missingProof: null};
  }

  const {status, payload} = error;

  // 428 é sempre precondição. 403 só é quando o código diz que é: 403 também
  // significa "esta conta não pode", e mandar o Thiago confirmar o passkey não
  // resolveria isso, só o faria repetir uma ação que vai ser recusada de novo.
  if (
    status === 428 ||
    (status === 403 && CODIGOS_DE_REAUTENTICACAO.has(payload.code)) ||
    (status === 403 && CODIGOS_DE_TITULAR.has(payload.code))
  ) {
    const missingProof = precondicao(status, payload.code, context);
    return {
      message:
        missingProof === 'passkey_reauth'
          ? MENSAGEM.reauth
          : (MENSAGEM_DO_TITULAR[payload.code] ?? MENSAGEM.titular),
      missingProof,
    };
  }

  if (MENSAGEM_POR_CODIGO[payload.code]) {
    return {message: MENSAGEM_POR_CODIGO[payload.code]!, missingProof: null};
  }

  if (payload.code === CODE_NOT_REDEEMABLE) {
    // Recusa uniforme, de propósito: inexistente, usado, vencido, revogado, de
    // outra conversa ou sem consentimento devolvem todos a MESMA frase.
    return {
      message:
        'Este código não pode ser resgatado. Peça um novo ao titular pelo canal que já está em uso.',
      missingProof: null,
    };
  }

  return {
    message:
      status !== null && POR_STATUS[status]
        ? POR_STATUS[status]!
        : MENSAGEM.generico,
    missingProof: null,
  };
};
